import type { DatabaseEngineId } from '@/core/engines';
import { isFlagSet } from '@/core/utils/dbFlags';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';

export type ScriptableDatabaseObjectType = 'view' | 'stored-procedure' | 'function';
export type ScriptMode = 'create' | 'alter';

export type RoutineParameter = {
  name: string;
  dataType: string;
  direction: 'IN' | 'OUT' | 'INOUT';
};

type NormalizedEngine = 'mssql' | 'pgsql' | 'sqlite' | 'mysql' | 'oracle';

function normalizeEngine(engine?: DatabaseEngineId | string): NormalizedEngine {
  if (engine === 'pgsql') return 'pgsql';
  if (engine === 'sqlite') return 'sqlite';
  if (engine === 'mysql') return 'mysql';
  if (engine === 'oracle') return 'oracle';
  return 'mssql';
}

function escapeSqlLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

function parseNumericId(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function getMssqlModuleTypeFilter(objectType: ScriptableDatabaseObjectType): string {
  switch (objectType) {
    case 'view':
      return "('V')";
    case 'stored-procedure':
      return "('P','PC')";
    case 'function':
      return "('FN','IF','TF','AF')";
    default: {
      const exhaustive: never = objectType;
      return exhaustive;
    }
  }
}

function buildMssqlDefinitionQuery(schema: string, name: string, objectType: ScriptableDatabaseObjectType): string {
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(name);
  const typeFilter = getMssqlModuleTypeFilter(objectType);

  return `
    SELECT m.definition AS definition
    FROM sys.sql_modules m
    JOIN sys.objects o ON m.object_id = o.object_id
    JOIN sys.schemas s ON o.schema_id = s.schema_id
    WHERE s.name = '${s}'
      AND o.name = '${n}'
      AND o.type IN ${typeFilter};
  `;
}

function buildMssqlRoutineParametersQuery(schema: string, name: string, objectType: Exclude<ScriptableDatabaseObjectType, 'view'>): string {
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(name);
  const typeFilter = getMssqlModuleTypeFilter(objectType);

  return `
    SELECT
      p.name AS parameter_name,
      TYPE_NAME(p.user_type_id) AS data_type,
      p.is_output AS is_output,
      p.parameter_id AS parameter_id
    FROM sys.parameters p
    JOIN sys.objects o ON p.object_id = o.object_id
    JOIN sys.schemas s ON o.schema_id = s.schema_id
    WHERE s.name = '${s}'
      AND o.name = '${n}'
      AND o.type IN ${typeFilter}
      AND p.parameter_id > 0
    ORDER BY p.parameter_id;
  `;
}

/**
 * Resolve a PostgreSQL object's OID from schema + name + type.
 *
 * Every PostgreSQL definition function below is OID-taking, but not every
 * caller has an OID — a `DependencyItem` carries no `objectId` at all, so
 * before this lookup existed "Open definition" / "Script as CREATE" from a
 * dependency row failed outright on PostgreSQL (F08).
 *
 * Names are matched verbatim: `pg_namespace.nspname` / `pg_class.relname` /
 * `pg_proc.proname` already store the case-folded form for unquoted identifiers
 * and the exact form for quoted ones, and callers read those same catalogs, so
 * no folding is applied here.
 *
 * Overloads: PostgreSQL allows several routines to share one name and a
 * name-only lookup cannot tell them apart. Every match is returned (no
 * `LIMIT`) so `resolvePgsqlObjectOid` can *say so* — the lowest OID is used,
 * and the caller prepends a warning naming the overload count rather than
 * silently opening an arbitrary body. Callers that know the exact routine (the
 * Object Explorer) pass `objectId` and never reach this fallback.
 */
function buildPgsqlObjectOidQuery(
  objectType: ScriptableDatabaseObjectType,
  schema: string,
  name: string,
): string {
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(name);

  if (objectType === 'view') {
    // relkind 'm' is included: pg_get_viewdef() works on materialized views too.
    // relname is unique per namespace, so this can only ever match one row.
    return `
      SELECT c.oid::text AS oid
      FROM pg_class c
      JOIN pg_namespace n ON n.oid = c.relnamespace
      WHERE n.nspname = '${s}' AND c.relname = '${n}' AND c.relkind IN ('v', 'm')
      ORDER BY c.oid;
    `;
  }

  // prokind 'p' = procedure, 'f' = plain function. Aggregates ('a') and window
  // functions ('w') are excluded on purpose — pg_get_functiondef() rejects them,
  // and a clean "not found" beats a raw server error.
  const prokind = objectType === 'stored-procedure' ? 'p' : 'f';
  return `
    SELECT p.oid::text AS oid
    FROM pg_proc p
    JOIN pg_namespace n ON n.oid = p.pronamespace
    WHERE n.nspname = '${s}' AND p.proname = '${n}' AND p.prokind = '${prokind}'
    ORDER BY p.oid;
  `;
}

/**
 * Runs the lookup above. `matchCount > 1` means the name is overloaded and the
 * returned `oid` is only the lowest-numbered candidate — see the caller, which
 * turns that into a visible warning at the top of the generated script.
 */
async function resolvePgsqlObjectOid(options: {
  connectionId: string;
  database: string;
  objectType: ScriptableDatabaseObjectType;
  schema: string;
  name: string;
}): Promise<{ oid?: number; matchCount: number; error?: string }> {
  const result = await executeQueryV2({
    connectionId: options.connectionId,
    database: options.database,
    sql: buildPgsqlObjectOidQuery(options.objectType, options.schema, options.name),
    executionId: createExplorerExecutionId(options.connectionId),
  });

  if (!result.success) {
    return { matchCount: 0, error: result.error?.message || 'Query failed' };
  }

  const rows = result.resultSets?.[0]?.rows ?? [];
  const first = rows[0]?.oid;
  return {
    oid: parseNumericId(first == null ? undefined : String(first)),
    matchCount: rows.length,
  };
}

function buildPgsqlViewDefinitionQuery(oid: number): string {
  return `SELECT pg_get_viewdef(${oid}, true) AS definition;`;
}

function buildPgsqlRoutineDefinitionQuery(oid: number): string {
  return `SELECT pg_get_functiondef(${oid}) AS definition;`;
}

function buildPgsqlRoutineIdentityArgsQuery(oid: number): string {
  return `SELECT pg_get_function_identity_arguments(${oid}) AS identity_arguments;`;
}

function buildPgsqlRoutineParametersQuery(oid: number): string {
  return `
    SELECT
      i.ordinality AS position,
      COALESCE(arg_names.name, '') AS parameter_name,
      pg_catalog.format_type(i.argtype, NULL) AS data_type,
      COALESCE(arg_modes.mode, 'i') AS mode
    FROM pg_proc p
    LEFT JOIN LATERAL unnest(COALESCE(p.proallargtypes, p.proargtypes::oid[])) WITH ORDINALITY AS i(argtype, ordinality)
      ON true
    LEFT JOIN LATERAL unnest(COALESCE(p.proargnames, ARRAY[]::text[])) WITH ORDINALITY AS arg_names(name, ordinality)
      ON arg_names.ordinality = i.ordinality
    LEFT JOIN LATERAL unnest(COALESCE(p.proargmodes, ARRAY[]::"char"[])) WITH ORDINALITY AS arg_modes(mode, ordinality)
      ON arg_modes.ordinality = i.ordinality
    WHERE p.oid = ${oid}
    ORDER BY i.ordinality;
  `;
}

function quoteMysqlIdentifier(name: string): string {
  return `\`${name.replace(/`/g, '``')}\``;
}

function qualifyMysqlObjectName(schema: string, name: string): string {
  const trimmedSchema = schema.trim();
  if (!trimmedSchema) return quoteMysqlIdentifier(name);
  return `${quoteMysqlIdentifier(trimmedSchema)}.${quoteMysqlIdentifier(name)}`;
}

function normalizeMysqlDefinition(definition: string): string {
  const trimmed = definition.trim();
  if (!trimmed) return trimmed;
  return trimmed.endsWith(';') ? trimmed : `${trimmed};`;
}

function toMysqlCreateOrReplaceView(createStatement: string): string {
  const trimmed = createStatement.trim().replace(/;\s*$/, '');
  if (!trimmed) return '';
  if (/^CREATE\s+OR\s+REPLACE\s+/i.test(trimmed)) return `${trimmed};`;
  if (!/^CREATE\s+/i.test(trimmed)) return `${trimmed};`;
  return `${trimmed.replace(/^CREATE\s+/i, 'CREATE OR REPLACE ')};`;
}

function buildMysqlDefinitionQuery(objectType: ScriptableDatabaseObjectType, schema: string, name: string): string {
  const qualified = qualifyMysqlObjectName(schema, name);
  switch (objectType) {
    case 'view':
      return `SHOW CREATE VIEW ${qualified};`;
    case 'stored-procedure':
      return `SHOW CREATE PROCEDURE ${qualified};`;
    case 'function':
      return `SHOW CREATE FUNCTION ${qualified};`;
  }
}

function getMysqlDefinitionColumnName(objectType: ScriptableDatabaseObjectType): string {
  switch (objectType) {
    case 'view':
      return 'Create View';
    case 'stored-procedure':
      return 'Create Procedure';
    case 'function':
      return 'Create Function';
  }
}

// ============================================================================
// Oracle helpers
// ============================================================================

function quoteOracleIdentifier(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function buildOracleViewDefinitionQuery(schema: string, name: string): string {
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(name);
  return `SELECT TEXT AS "definition" FROM ALL_VIEWS WHERE OWNER = '${s}' AND VIEW_NAME = '${n}'`;
}

function buildOracleSourceQuery(schema: string, name: string, objectType: string): string {
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(name);
  return `SELECT TEXT AS "text" FROM ALL_SOURCE WHERE OWNER = '${s}' AND NAME = '${n}' AND TYPE = '${objectType}' ORDER BY LINE`;
}

function buildOracleRoutineParametersQuery(schema: string, name: string, packageName?: string, overloadIndex?: number): string {
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(name);
  if (packageName) {
    const p = escapeSqlLiteral(packageName);
    const overloadFilter = overloadIndex != null ? `AND OVERLOAD = '${overloadIndex}'` : '';
    return `
      SELECT
        ARGUMENT_NAME AS "parameter_name",
        DATA_TYPE AS "data_type",
        IN_OUT AS "in_out",
        POSITION AS "position"
      FROM ALL_ARGUMENTS
      WHERE OWNER = '${s}'
        AND PACKAGE_NAME = '${p}'
        AND OBJECT_NAME = '${n}'
        ${overloadFilter}
        AND POSITION > 0
      ORDER BY POSITION
    `;
  }
  return `
    SELECT
      ARGUMENT_NAME AS "parameter_name",
      DATA_TYPE AS "data_type",
      IN_OUT AS "in_out",
      POSITION AS "position"
    FROM ALL_ARGUMENTS
    WHERE OWNER = '${s}'
      AND OBJECT_NAME = '${n}'
      AND PACKAGE_NAME IS NULL
      AND POSITION > 0
    ORDER BY POSITION
  `;
}

function quoteSqliteIdentifier(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function buildSqliteDefinitionQuery(type: 'view', name: string): string {
  const n = escapeSqlLiteral(name);
  return `SELECT sql AS definition FROM sqlite_master WHERE type = '${type}' AND name = '${n}';`;
}

function normalizeSqliteDefinition(definition: string): string {
  const trimmed = definition.trim();
  if (!trimmed) return trimmed;
  return trimmed.endsWith(';') ? trimmed : `${trimmed};`;
}

async function fetchFirstRowValue(
  connectionId: string,
  database: string,
  sql: string,
  columnName: string,
): Promise<{ value?: string; error?: string }> {
  const result = await executeQueryV2({
    connectionId,
    database,
    sql,
    executionId: createExplorerExecutionId(connectionId),
  });

  if (!result.success) {
    return { error: result.error?.message || 'Query failed' };
  }

  const row = result.resultSets?.[0]?.rows?.[0];
  const raw = row?.[columnName] as unknown;
  if (raw == null) {
    return { value: undefined };
  }

  return { value: String(raw) };
}

export function rewriteMssqlCreateToAlter(definition: string, objectType: ScriptableDatabaseObjectType): string {
  const keyword = objectType === 'stored-procedure' ? '(?:PROCEDURE|PROC)' : objectType === 'view' ? 'VIEW' : 'FUNCTION';
  const createOrAlter = new RegExp(`\\bCREATE\\s+OR\\s+ALTER\\s+${keyword}\\b`, 'i');
  const create = new RegExp(`\\bCREATE\\s+${keyword}\\b`, 'i');

  if (createOrAlter.test(definition)) {
    return definition.replace(createOrAlter, (match) => match.replace(/CREATE\\s+OR\\s+ALTER/i, 'ALTER'));
  }
  if (create.test(definition)) {
    return definition.replace(create, (match) => match.replace(/CREATE/i, 'ALTER'));
  }
  return definition;
}

export async function fetchObjectDefinitionScript(options: {
  connectionId: string;
  database: string;
  engine?: DatabaseEngineId | string;
  objectType: ScriptableDatabaseObjectType;
  schema: string;
  name: string;
  objectId?: string;
  mode: ScriptMode;
}): Promise<{ script: string; error?: string }> {
  const engine = normalizeEngine(options.engine);

  if (engine === 'sqlite') {
    if (options.objectType !== 'view') {
      const message = `SQLite does not support ${options.objectType} objects`;
      return { script: `-- ${message}\n`, error: message };
    }

    const sql = buildSqliteDefinitionQuery('view', options.name);
    const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, 'definition');
    if (error) return { script: `-- Error fetching view definition: ${error}\n`, error };
    if (!value) return { script: `-- Definition not found for ${options.name}\n` };

    const qualified = options.schema
      ? `${quoteSqliteIdentifier(options.schema)}.${quoteSqliteIdentifier(options.name)}`
      : quoteSqliteIdentifier(options.name);

    const createScript = normalizeSqliteDefinition(value);
    if (options.mode === 'alter') {
      return {
        script: `-- SQLite does not support ALTER VIEW.\n-- This script drops and recreates the view.\nDROP VIEW IF EXISTS ${qualified};\n\n${createScript}\n`,
      };
    }

    return { script: createScript };
  }

  if (engine === 'mysql') {
    const sql = buildMysqlDefinitionQuery(options.objectType, options.schema, options.name);
    const columnName = getMysqlDefinitionColumnName(options.objectType);
    const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, columnName);
    if (error) return { script: `-- Error fetching definition: ${error}\n`, error };
    if (!value) return { script: `-- Definition not found for ${options.name}\n` };

    if (options.mode === 'alter') {
      const createScript = normalizeMysqlDefinition(value);

      // Views support CREATE OR REPLACE (preferred over DROP+CREATE)
      if (options.objectType === 'view') {
        const alterScript = toMysqlCreateOrReplaceView(createScript);
        return {
          script: `-- MySQL uses CREATE OR REPLACE VIEW for definition changes.\n${alterScript}\n`,
        };
      }

      // Procedures/functions: MySQL doesn't support CREATE OR REPLACE, use DROP+CREATE
      const dropKeyword = options.objectType === 'stored-procedure' ? 'PROCEDURE' : 'FUNCTION';
      const qualified = qualifyMysqlObjectName(options.schema, options.name);
      return {
        script: `-- MySQL does not support ALTER ${dropKeyword} for definition changes.\n-- This script drops and recreates the object.\nDROP ${dropKeyword} IF EXISTS ${qualified};\n\n${createScript}\n`,
      };
    }

    return { script: normalizeMysqlDefinition(value) };
  }

  if (engine === 'oracle') {
    if (options.objectType === 'view') {
      const sql = buildOracleViewDefinitionQuery(options.schema, options.name);
      const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, 'definition');
      if (error) return { script: `-- Error fetching view definition: ${error}\n`, error };
      if (!value) return { script: `-- Definition not found for ${options.schema}.${options.name}\n` };

      const qualified = `${quoteOracleIdentifier(options.schema)}.${quoteOracleIdentifier(options.name)}`;
      const viewBody = value.trim().replace(/;\s*$/, '');
      return { script: `CREATE OR REPLACE VIEW ${qualified} AS\n${viewBody};\n` };
    }

    // Procedures and functions: reconstruct from ALL_SOURCE
    const oracleObjectType = options.objectType === 'stored-procedure' ? 'PROCEDURE' : 'FUNCTION';
    const sql = buildOracleSourceQuery(options.schema, options.name, oracleObjectType);
    const result = await executeQueryV2({
      connectionId: options.connectionId,
      database: options.database,
      sql,
      executionId: createExplorerExecutionId(options.connectionId),
    });

    if (!result.success) {
      return { script: `-- Error fetching source: ${result.error?.message}\n`, error: result.error?.message };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    if (rows.length === 0) {
      return { script: `-- Source not found for ${options.schema}.${options.name}\n` };
    }

    const sourceLines = rows.map((row) => String(row.text ?? '')).join('');
    return { script: `CREATE OR REPLACE ${sourceLines.trim()}\n/\n` };
  }

  if (engine === 'mssql') {
    const sql = buildMssqlDefinitionQuery(options.schema, options.name, options.objectType);
    const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, 'definition');
    if (error) return { script: `-- Error fetching definition: ${error}\n`, error };
    if (!value) return { script: `-- Definition not found for ${options.schema}.${options.name}\n` };

    const script = options.mode === 'alter'
      ? rewriteMssqlCreateToAlter(value, options.objectType)
      : value;
    return { script };
  }

  // Dependency Viewer rows (tree and graph) know an object only by
  // schema + name, so resolve the OID from the catalog instead of failing (F08).
  let oid = parseNumericId(options.objectId);
  // Prepended to the generated script when the name turned out to be
  // overloaded: the body below belongs to ONE of the candidates, and the user
  // must be told rather than left to assume it is the one they clicked.
  let overloadNotice = '';
  if (!oid) {
    const resolved = await resolvePgsqlObjectOid({
      connectionId: options.connectionId,
      database: options.database,
      objectType: options.objectType,
      schema: options.schema,
      name: options.name,
    });
    if (resolved.error) {
      const message = `Error resolving object id (OID): ${resolved.error}`;
      return { script: `-- ${message}\n`, error: message };
    }
    oid = resolved.oid;
    if (oid && resolved.matchCount > 1) {
      overloadNotice = `-- Warning: ${resolved.matchCount} overloads of ${options.schema}.${options.name} exist; showing OID ${oid}.\n`;
    }
  }
  if (!oid) {
    // Loud on purpose: the caller believes this object exists, so a silent
    // empty script would hide a dropped object / wrong type / missing grant.
    const message = `Definition not found for ${options.schema}.${options.name}`;
    return { script: `-- ${message}\n`, error: message };
  }

  if (options.objectType === 'view') {
    const sql = buildPgsqlViewDefinitionQuery(oid);
    const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, 'definition');
    if (error) return { script: `-- Error fetching view definition: ${error}\n`, error };
    if (!value) return { script: `-- Definition not found for ${options.schema}.${options.name}\n` };

    const qualified = `"${options.schema.replace(/"/g, '""')}"."${options.name.replace(/"/g, '""')}"`;
    return {
      script: `${overloadNotice}CREATE OR REPLACE VIEW ${qualified} AS\n${value.trim().replace(/;\\s*$/, '')};\n`,
    };
  }

  const sql = buildPgsqlRoutineDefinitionQuery(oid);
  const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, 'definition');
  if (error) return { script: `-- Error fetching definition: ${error}\n`, error };
  if (!value) return { script: `-- Definition not found for ${options.schema}.${options.name}\n` };

  return { script: `${overloadNotice}${value}` };
}

/**
 * Fetch Oracle-specific object scripts (packages, sequences, synonyms, etc.)
 * These don't go through the shared ScriptProvider / ScriptableDatabaseObjectType system.
 */
export async function fetchOracleObjectScript(options: {
  connectionId: string;
  database: string;
  schema: string;
  name: string;
  objectType: 'package' | 'sequence' | 'synonym' | 'materialized-view' | 'type' | 'db-link' | 'property-graph';
  mode: 'create' | 'drop';
}): Promise<{ script: string; error?: string }> {
  const qualified = `${quoteOracleIdentifier(options.schema)}.${quoteOracleIdentifier(options.name)}`;

  if (options.mode === 'drop') {
    const dropKeywordMap: Record<string, string> = {
      'package': 'PACKAGE',
      'sequence': 'SEQUENCE',
      'synonym': 'SYNONYM',
      'materialized-view': 'MATERIALIZED VIEW',
      'type': 'TYPE',
      'db-link': 'DATABASE LINK',
      'property-graph': 'PROPERTY GRAPH',
    };
    const keyword = dropKeywordMap[options.objectType] || 'OBJECT';
    return { script: `DROP ${keyword} ${qualified};\n` };
  }

  // CREATE mode - fetch definitions from the database
  if (options.objectType === 'package') {
    // Fetch both PACKAGE (spec) and PACKAGE BODY source
    const specSql = buildOracleSourceQuery(options.schema, options.name, 'PACKAGE');
    const bodySql = buildOracleSourceQuery(options.schema, options.name, 'PACKAGE BODY');

    const [specResult, bodyResult] = await Promise.all([
      executeQueryV2({ connectionId: options.connectionId, database: options.database, sql: specSql, executionId: createExplorerExecutionId(options.connectionId) }),
      executeQueryV2({ connectionId: options.connectionId, database: options.database, sql: bodySql, executionId: createExplorerExecutionId(options.connectionId) }),
    ]);

    const parts: string[] = [];

    if (specResult.success && specResult.resultSets?.[0]?.rows?.length) {
      const specLines = specResult.resultSets[0].rows.map((row) => String(row.text ?? '')).join('');
      parts.push(`CREATE OR REPLACE ${specLines.trim()}\n/`);
    }

    if (bodyResult.success && bodyResult.resultSets?.[0]?.rows?.length) {
      const bodyLines = bodyResult.resultSets[0].rows.map((row) => String(row.text ?? '')).join('');
      parts.push(`CREATE OR REPLACE ${bodyLines.trim()}\n/`);
    }

    if (parts.length === 0) {
      return { script: `-- Package source not found for ${options.schema}.${options.name}\n` };
    }

    return { script: parts.join('\n\n') + '\n' };
  }

  if (options.objectType === 'property-graph') {
    // DBMS_METADATA.GET_DDL returns a CLOB; the Oracle adapter fetches CLOBs
    // as strings (oracleAdapterUtils DB_TYPE_CLOB -> DB_TYPE_VARCHAR).
    const s = escapeSqlLiteral(options.schema);
    const n = escapeSqlLiteral(options.name);
    const sql = `SELECT DBMS_METADATA.GET_DDL('PROPERTY_GRAPH', '${n}', '${s}') AS "definition" FROM DUAL`;
    const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, 'definition');
    if (error) return { script: `-- Error fetching property graph DDL: ${error}\n`, error };
    if (!value) return { script: `-- DDL not found for ${options.schema}.${options.name}\n` };
    const ddl = value.trim();
    return { script: ddl.endsWith(';') ? `${ddl}\n` : `${ddl};\n` };
  }

  if (options.objectType === 'sequence') {
    const s = escapeSqlLiteral(options.schema);
    const n = escapeSqlLiteral(options.name);
    const sql = `
      SELECT
        INCREMENT_BY AS "increment_by",
        MIN_VALUE AS "min_value",
        MAX_VALUE AS "max_value",
        CACHE_SIZE AS "cache_size",
        CYCLE_FLAG AS "cycle_flag",
        ORDER_FLAG AS "order_flag",
        LAST_NUMBER AS "last_number"
      FROM ALL_SEQUENCES
      WHERE SEQUENCE_OWNER = '${s}' AND SEQUENCE_NAME = '${n}'
    `;

    const result = await executeQueryV2({
      connectionId: options.connectionId,
      database: options.database,
      sql,
      executionId: createExplorerExecutionId(options.connectionId),
    });

    if (!result.success || !result.resultSets?.[0]?.rows?.length) {
      return { script: `CREATE SEQUENCE ${qualified};\n` };
    }

    const row = result.resultSets[0].rows[0];
    const lines = [`CREATE SEQUENCE ${qualified}`];
    lines.push(`    INCREMENT BY ${row.increment_by}`);
    lines.push(`    START WITH ${row.last_number}`);
    lines.push(`    MINVALUE ${row.min_value}`);
    lines.push(`    MAXVALUE ${row.max_value}`);
    lines.push(`    ${Number(row.cache_size) > 0 ? `CACHE ${row.cache_size}` : 'NOCACHE'}`);
    lines.push(`    ${row.cycle_flag === 'Y' ? 'CYCLE' : 'NOCYCLE'}`);
    lines.push(`    ${row.order_flag === 'Y' ? 'ORDER' : 'NOORDER'};`);

    return { script: lines.join('\n') + '\n' };
  }

  if (options.objectType === 'synonym') {
    const s = escapeSqlLiteral(options.schema);
    const n = escapeSqlLiteral(options.name);
    const sql = `
      SELECT
        TABLE_OWNER AS "table_owner",
        TABLE_NAME AS "table_name",
        DB_LINK AS "db_link"
      FROM ALL_SYNONYMS
      WHERE OWNER = '${s}' AND SYNONYM_NAME = '${n}'
    `;

    const result = await executeQueryV2({
      connectionId: options.connectionId,
      database: options.database,
      sql,
      executionId: createExplorerExecutionId(options.connectionId),
    });

    if (!result.success || !result.resultSets?.[0]?.rows?.length) {
      return { script: `CREATE SYNONYM ${qualified} FOR <target_object>;\n` };
    }

    const row = result.resultSets[0].rows[0];
    const target = row.db_link
      ? `${quoteOracleIdentifier(String(row.table_owner))}.${quoteOracleIdentifier(String(row.table_name))}@${row.db_link}`
      : `${quoteOracleIdentifier(String(row.table_owner))}.${quoteOracleIdentifier(String(row.table_name))}`;

    return { script: `CREATE OR REPLACE SYNONYM ${qualified} FOR ${target};\n` };
  }

  if (options.objectType === 'type') {
    const sql = buildOracleSourceQuery(options.schema, options.name, 'TYPE');
    const result = await executeQueryV2({
      connectionId: options.connectionId,
      database: options.database,
      sql,
      executionId: createExplorerExecutionId(options.connectionId),
    });

    if (!result.success || !result.resultSets?.[0]?.rows?.length) {
      return { script: `-- Type source not found for ${options.schema}.${options.name}\n` };
    }

    const sourceLines = result.resultSets[0].rows.map((row) => String(row.text ?? '')).join('');
    return { script: `CREATE OR REPLACE ${sourceLines.trim()}\n/\n` };
  }

  // Materialized view, DB link - basic templates
  return { script: `-- Script generation for ${options.objectType} is not yet supported\n-- Object: ${qualified}\n` };
}

function quotePgsqlIdentifier(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

/**
 * CREATE / DROP scripts for PostgreSQL 19+ SQL property graphs.
 * CREATE DDL comes from pg_get_propgraphdef(); the graph oid is resolved by
 * schema + name via pg_class/pg_namespace (avoids regclass cast failure
 * modes — a missing graph yields NULL, handled as "not found").
 */
export async function fetchPgsqlPropertyGraphScript(options: {
  connectionId: string;
  database: string;
  schema: string;
  name: string;
  mode: 'create' | 'drop';
}): Promise<{ script: string; error?: string }> {
  const qualified = `${quotePgsqlIdentifier(options.schema)}.${quotePgsqlIdentifier(options.name)}`;

  if (options.mode === 'drop') {
    return { script: `DROP PROPERTY GRAPH IF EXISTS ${qualified};\n` };
  }

  const s = escapeSqlLiteral(options.schema);
  const n = escapeSqlLiteral(options.name);
  const sql = `
    SELECT pg_get_propgraphdef((
      SELECT c.oid
      FROM pg_class c
      JOIN pg_namespace n ON c.relnamespace = n.oid
      WHERE c.relkind = 'g' AND n.nspname = '${s}' AND c.relname = '${n}'
    )) AS "definition"
  `;
  const { value, error } = await fetchFirstRowValue(options.connectionId, options.database, sql, 'definition');
  if (error) return { script: `-- Error fetching property graph DDL: ${error}\n`, error };
  if (!value) return { script: `-- DDL not found for ${options.schema}.${options.name}\n` };
  const ddl = value.trim();
  // pg_get_propgraphdef emits no trailing semicolon (verified on 19beta1)
  return { script: ddl.endsWith(';') ? `${ddl}\n` : `${ddl};\n` };
}

/**
 * CREATE DDL for a PostgreSQL 19+ database via the server-native
 * `pg_get_database_ddl(regdatabase)` function. It returns `SETOF text` — e.g.
 * dvdrental emits two rows (`CREATE DATABASE …` + `ALTER DATABASE … OWNER TO …`)
 * — so ALL rows are aggregated (newline-joined). Returns `null` on any error
 * (function missing on PG < 19, connection error) so callers fall back to the
 * hand-built script generator.
 */
export async function fetchPgsqlDatabaseDdl(params: {
  connectionId: string;
  database: string;
}): Promise<string | null> {
  const d = escapeSqlLiteral(params.database);
  const sql = `SELECT pg_get_database_ddl('${d}') AS ddl`;
  try {
    const result = await executeQueryV2({
      connectionId: params.connectionId,
      database: params.database,
      sql,
      executionId: createExplorerExecutionId(params.connectionId),
    });
    if (!result.success) return null;
    const rows = result.resultSets?.[0]?.rows ?? [];
    const ddl = rows
      .map((row) => String(row.ddl ?? ''))
      .filter((line) => line.length > 0)
      .join('\n')
      .trim();
    return ddl.length > 0 ? ddl : null;
  } catch {
    return null;
  }
}

export async function fetchRoutineParameters(options: {
  connectionId: string;
  database: string;
  engine?: DatabaseEngineId | string;
  routineType: 'stored-procedure' | 'function';
  schema: string;
  name: string;
  objectId?: string;
  packageName?: string;
  overloadIndex?: number;
}): Promise<{ parameters: RoutineParameter[]; identityArguments?: string; error?: string }> {
  const engine = normalizeEngine(options.engine);

  if (engine === 'sqlite') {
    const message = `SQLite does not support ${options.routineType} objects`;
    return { parameters: [], error: message };
  }

  if (engine === 'mysql') {
    // MySQL routine parameters via information_schema
    const routineType = options.routineType === 'stored-procedure' ? 'PROCEDURE' : 'FUNCTION';
    const n = escapeSqlLiteral(options.name);
    const s = escapeSqlLiteral(options.schema);
    const sql = `
      SELECT
        PARAMETER_NAME AS parameter_name,
        DATA_TYPE AS data_type,
        PARAMETER_MODE AS parameter_mode,
        ORDINAL_POSITION AS ordinal_position
      FROM information_schema.PARAMETERS
      WHERE SPECIFIC_SCHEMA = '${s}'
        AND SPECIFIC_NAME = '${n}'
        AND ROUTINE_TYPE = '${routineType}'
        AND ORDINAL_POSITION > 0
      ORDER BY ORDINAL_POSITION;
    `;
    const result = await executeQueryV2({
      connectionId: options.connectionId,
      database: options.database,
      sql,
      executionId: createExplorerExecutionId(options.connectionId),
    });

    if (!result.success) {
      return { parameters: [], error: result.error?.message || 'Failed to fetch parameters' };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const parameters: RoutineParameter[] = rows.map((row) => ({
      name: String(row.parameter_name || ''),
      dataType: String(row.data_type || ''),
      direction: row.parameter_mode === 'OUT' ? 'OUT' : row.parameter_mode === 'INOUT' ? 'INOUT' : 'IN',
    }));

    return { parameters };
  }

  if (engine === 'oracle') {
    const sql = buildOracleRoutineParametersQuery(options.schema, options.name, options.packageName, options.overloadIndex);
    const result = await executeQueryV2({
      connectionId: options.connectionId,
      database: options.database,
      sql,
      executionId: createExplorerExecutionId(options.connectionId),
    });

    if (!result.success) {
      return { parameters: [], error: result.error?.message || 'Failed to fetch parameters' };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const parameters: RoutineParameter[] = rows.map((row) => ({
      name: String(row.parameter_name || ''),
      dataType: String(row.data_type || ''),
      direction: row.in_out === 'OUT' ? 'OUT' : row.in_out === 'IN/OUT' ? 'INOUT' : 'IN',
    }));

    return { parameters };
  }

  if (engine === 'mssql') {
    const sql = buildMssqlRoutineParametersQuery(options.schema, options.name, options.routineType);
    const result = await executeQueryV2({
      connectionId: options.connectionId,
      database: options.database,
      sql,
      executionId: createExplorerExecutionId(options.connectionId),
    });

    if (!result.success) {
      return { parameters: [], error: result.error?.message || 'Failed to fetch parameters' };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const parameters: RoutineParameter[] = rows.map((row) => ({
      name: String(row.parameter_name || ''),
      dataType: String(row.data_type || ''),
      // `sys.parameters.is_output` is a BIT: the native mssql driver returns a
      // real boolean, but the SQL Tools Service text path delivers it as a
      // "0"/"1" (or "True"/"False") STRING. Without isFlagSet tolerance every
      // OUTPUT parameter is misreported as an input parameter on the STS route.
      direction: isFlagSet(row.is_output) ? 'OUT' : 'IN',
    }));

    return { parameters };
  }

  const oid = parseNumericId(options.objectId);
  if (!oid) {
    const message = 'Missing/invalid object id (OID) for PostgreSQL routine';
    return { parameters: [], error: message };
  }

  const paramsSql = buildPgsqlRoutineParametersQuery(oid);
  const paramsResult = await executeQueryV2({
    connectionId: options.connectionId,
    database: options.database,
    sql: paramsSql,
    executionId: createExplorerExecutionId(options.connectionId),
  });

  if (!paramsResult.success) {
    return { parameters: [], error: paramsResult.error?.message || 'Failed to fetch parameters' };
  }

  const rows = paramsResult.resultSets?.[0]?.rows || [];
  const toDirection = (mode: unknown): RoutineParameter['direction'] => {
    switch (String(mode || 'i')) {
      case 'o':
        return 'OUT';
      case 'b':
        return 'INOUT';
      default:
        return 'IN';
    }
  };

  const parameters: RoutineParameter[] = rows.map((row) => ({
    name: String(row.parameter_name || ''),
    dataType: String(row.data_type || ''),
    direction: toDirection(row.mode),
  }));

  const identitySql = buildPgsqlRoutineIdentityArgsQuery(oid);
  const { value: identityArguments } = await fetchFirstRowValue(
    options.connectionId,
    options.database,
    identitySql,
    'identity_arguments',
  );

  return { parameters, identityArguments: identityArguments ?? '' };
}
